import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Anchor, ArrowLeft, FileText, FileSpreadsheet, AlertTriangle, ChevronDown, ChevronUp, Save, CalendarDays, Download, BarChart3 } from "lucide-react";
import OrientationMakeupPanel from "@/components/OrientationMakeupPanel";
import api, { getToken, getLevel } from "@/lib/api";
import { toast } from "sonner";

const TIER_STYLES = {
  green: { pill: "bg-emerald-100 text-emerald-800", row: "border-l-4 border-l-emerald-500" },
  teal: { pill: "bg-teal-100 text-teal-800", row: "border-l-4 border-l-teal-500" },
  amber: { pill: "bg-amber-100 text-amber-800", row: "border-l-4 border-l-amber-500" },
  orange: { pill: "bg-orange-100 text-orange-800", row: "border-l-4 border-l-orange-500" },
  red: { pill: "bg-red-600 text-white font-bold", row: "border-l-4 border-l-red-600" },
};

function weekday(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", { weekday: "short" });
}

function StaffNoteEditor({ memberId, seasonId, isAdmin }) {
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState(true);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!memberId || !seasonId) return;
    api.get(`/staff-notes/${memberId}`, { params: { season_id: seasonId } })
      .then((r) => { setNote(r.data?.notes || ""); setSaved(true); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [memberId, seasonId]);

  const save = async () => {
    try {
      await api.put(`/staff-notes/${memberId}`, { season_id: seasonId, notes: note });
      setSaved(true);
      toast.success("Note saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed (admin only)");
    }
  };

  if (!loaded) return <div className="text-xs text-slate-400">Loading note…</div>;

  return (
    <div className="mt-3">
      <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold flex items-center justify-between">
        <span>Admin Note</span>
        {!saved && <span className="text-amber-600 normal-case">Unsaved</span>}
      </label>
      <textarea
        value={note}
        readOnly={!isAdmin}
        onChange={(e) => { setNote(e.target.value); setSaved(false); }}
        className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm h-24 outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-slate-50"
        placeholder={isAdmin ? "Add a private note about this staff member for the season…" : "(Admin login required to add notes.)"}
        data-testid={`note-${memberId}`}
      />
      {isAdmin && (
        <button onClick={save} disabled={saved} className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-50" data-testid={`note-save-${memberId}`}>
          <Save className="w-3 h-3" /> Save Note
        </button>
      )}
    </div>
  );
}

export default function Reports() {
  const navigate = useNavigate();
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tierFilter, setTierFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});

  useEffect(() => { if (!getToken()) navigate("/"); }, [navigate]);

  useEffect(() => {
    api.get("/seasons").then((r) => {
      const items = r.data.items || [];
      setSeasons(items);
      const active = items.find((s) => s.status === "active") || items[0];
      if (active) setSeasonId(active.id || active._id);
    });
  }, []);

  const load = useCallback(async (sid) => {
    if (!sid) return;
    setLoading(true);
    try {
      const r = await api.get("/reports/staff-performance", { params: { season_id: sid } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load report");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (seasonId) load(seasonId); }, [seasonId, load]);

  const items = data?.items || [];
  const tiers = data?.tiers || [];

  const tierCounts = useMemo(() => {
    const c = { All: items.length };
    tiers.forEach((t) => { c[t.name] = 0; });
    items.forEach((p) => {
      const t = p?.rating?.tier;
      if (t) c[t] = (c[t] || 0) + 1;
    });
    return c;
  }, [items, tiers]);

  const filtered = useMemo(() => {
    let rs = items;
    if (tierFilter !== "All") rs = rs.filter((p) => p?.rating?.tier === tierFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rs = rs.filter((p) => (p.full_name || "").toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q));
    }
    return rs;
  }, [items, tierFilter, search]);

  const stats = useMemo(() => {
    if (!items.length) return { total: 0, avgAtt: 0, avgOnTime: 0 };
    const sumAtt = items.reduce((a, p) => a + ((p.percentages || {}).attendance_rate || 0), 0);
    const sumOT = items.reduce((a, p) => a + ((p.percentages || {}).on_time_rate || 0), 0);
    return {
      total: items.length,
      avgAtt: Math.round((sumAtt / items.length) * 10) / 10,
      avgOnTime: Math.round((sumOT / items.length) * 10) / 10,
    };
  }, [items]);

  const downloadFile = async (path, params, filename) => {
    try {
      const res = await api.get(path, { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
    } catch { toast.error("Download failed"); }
  };

  const isAdmin = ["admin", "leadership"].includes(getLevel());
  const [showTierTable, setShowTierTable] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} className="btn-touch inline-flex items-center gap-1 px-3 rounded-lg text-slate-700 hover:bg-slate-100" data-testid="reports-back">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </button>
            <img src="/logo.png" alt="Rolling River Day Camp" className="w-10 h-10 rounded-xl object-contain bg-white shadow-sm p-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Rolling River</div>
              <h1 className="font-display text-lg font-bold text-slate-900">Reports</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold">Season</span>
            <select value={seasonId} onChange={(e) => setSeasonId(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white" data-testid="reports-season-select">
              {seasons.map((s) => <option key={s.id || s._id} value={s.id || s._id}>{s.name}</option>)}
            </select>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-4 sm:px-6 py-6">
        {/* Reports index — every available report in one place */}
        <div className="mb-2 flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4 text-slate-500" />
          <h2 className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-bold">Available Reports</h2>
        </div>

        <AbsenceLatenessReportCard downloadFile={downloadFile} />
        <PlannedAbsencesGridCard downloadFile={downloadFile} />
        <AbsenceFormStatusCard downloadFile={downloadFile} />
        <OrientationMakeupPanel />
        <StaffPerformanceReportCard
          downloadFile={downloadFile}
          seasonId={seasonId}
          onToggleTable={() => setShowTierTable((v) => !v)}
          tableOpen={showTierTable}
        />

        {/* Inline Tier / Performance table — gated behind the View toggle so
            the Reports hub stays scannable for admins who only need the
            download. The table itself is unchanged. */}
        {showTierTable && (<>
        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <div className="rounded-xl bg-white border border-slate-200 p-4">
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold">Staff in Report</div>
            <div className="text-3xl font-display font-extrabold tabular-nums text-slate-900">{stats.total}</div>
          </div>
          <div className="rounded-xl bg-white border border-slate-200 p-4">
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold">Avg Attendance %</div>
            <div className="text-3xl font-display font-extrabold tabular-nums text-emerald-700">{stats.avgAtt}%</div>
          </div>
          <div className="rounded-xl bg-white border border-slate-200 p-4">
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold">Avg On-Time %</div>
            <div className="text-3xl font-display font-extrabold tabular-nums text-blue-700">{stats.avgOnTime}%</div>
          </div>
        </div>

        {/* Tier chips */}
        <div className="flex gap-2 flex-wrap mb-4 overflow-x-auto no-scrollbar">
          <button onClick={() => setTierFilter("All")} className={`btn-touch px-4 rounded-full text-sm font-semibold border ${tierFilter === "All" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`} data-testid="tier-filter-all">All <span className="ml-1 opacity-70 tabular-nums">{tierCounts.All || 0}</span></button>
          {tiers.map((t) => {
            const sty = TIER_STYLES[t.color] || TIER_STYLES.amber;
            return (
              <button key={t.name} onClick={() => setTierFilter(t.name)}
                className={`btn-touch px-4 rounded-full text-sm font-semibold border ${tierFilter === t.name ? "bg-slate-900 text-white border-slate-900" : `${sty.pill} border-transparent`}`}
                data-testid={`tier-filter-${t.color}`}
              >
                {t.name} <span className="ml-1 opacity-70 tabular-nums">{tierCounts[t.name] || 0}</span>
              </button>
            );
          })}
        </div>

        <input
          value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full md:w-96 px-3 py-2 rounded-lg border border-slate-200 text-sm mb-4"
          data-testid="reports-search"
        />

        {loading && <div className="text-center py-12 text-slate-500">Computing performance…</div>}

        {!loading && filtered.length === 0 && (
          <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center text-slate-500" data-testid="reports-empty">
            No data yet for this season. Performance reports become meaningful after several days of attendance are recorded.
          </div>
        )}

        {/* Table */}
        {!loading && filtered.length > 0 && (
          <div className="rounded-2xl bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Tier</th>
                  <th className="text-left px-3 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Name</th>
                  <th className="text-right px-3 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Days</th>
                  <th className="text-right px-3 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Present</th>
                  <th className="text-right px-3 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Late</th>
                  <th className="text-right px-3 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Absent</th>
                  <th className="text-right px-3 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Attend %</th>
                  <th className="text-right px-3 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">On-Time %</th>
                  <th className="text-right px-3 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Avg Arr</th>
                  <th className="text-right px-3 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Streak</th>
                  <th className="px-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const sty = TIER_STYLES[p?.rating?.color] || TIER_STYLES.amber;
                  const isOpen = !!expanded[p.member_id];
                  const t = p.totals || {}; const pct = p.percentages || {}; const pat = p.patterns || {};
                  return (
                    <>
                      <tr key={p.member_id} className={`border-t border-slate-100 hover:bg-slate-50/60 ${sty.row}`} data-testid={`perf-row-${p.member_id}`}>
                        <td className="px-3 py-3"><span className={`px-2 py-0.5 rounded text-[11px] font-bold ${sty.pill}`}>{p.rating?.tier}</span></td>
                        <td className="px-3 py-3">
                          <div className="font-semibold text-slate-900">{p.full_name}</div>
                          <div className="text-xs text-slate-500">{p.email}</div>
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums">{t.days_expected || 0}</td>
                        <td className="px-3 py-3 text-right tabular-nums">{t.days_present_on_time || 0}</td>
                        <td className="px-3 py-3 text-right tabular-nums">{(t.days_late || 0) + (t.days_late_arrival || 0)}</td>
                        <td className={`px-3 py-3 text-right tabular-nums ${(t.days_absent||0)>0?"text-red-700 font-semibold":""}`}>{t.days_absent || 0}</td>
                        <td className="px-3 py-3 text-right tabular-nums">{pct.attendance_rate ?? 0}%</td>
                        <td className="px-3 py-3 text-right tabular-nums">{pct.on_time_rate ?? 0}%</td>
                        <td className="px-3 py-3 text-right tabular-nums text-slate-600">{pat.average_arrival_time || "—"}</td>
                        <td className="px-3 py-3 text-right tabular-nums">{pat.consecutive_present_streak || 0}</td>
                        <td className="px-2">
                          <button onClick={() => setExpanded((e) => ({ ...e, [p.member_id]: !isOpen }))} className="p-1.5 rounded hover:bg-slate-100" data-testid={`perf-expand-${p.member_id}`}>
                            {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-50/40">
                          <td colSpan={11} className="px-6 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold mb-1">Arrival Patterns</div>
                                <div className="text-sm space-y-0.5 tabular-nums text-slate-700">
                                  <div>Avg: <span className="font-semibold">{pat.average_arrival_time || "—"}</span></div>
                                  <div>Most common: <span className="font-semibold">{pat.most_common_arrival_time || "—"}</span></div>
                                  <div>Earliest: <span className="font-semibold">{pat.earliest_arrival || "—"}</span></div>
                                  <div>Latest: <span className="font-semibold">{pat.latest_arrival || "—"}</span></div>
                                  <div>Avg departure: <span className="font-semibold">{pat.average_departure_time || "—"}</span></div>
                                </div>
                              </div>
                              <div>
                                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold mb-1">Streaks & Flags</div>
                                <div className="text-sm space-y-0.5 text-slate-700">
                                  <div>Current streak: <span className="font-semibold tabular-nums">{pat.consecutive_present_streak || 0}</span></div>
                                  <div>Longest streak: <span className="font-semibold tabular-nums">{pat.longest_present_streak || 0}</span></div>
                                  <div>Mon absences: <span className="tabular-nums">{pat.monday_absent_count || 0}</span> · Fri: <span className="tabular-nums">{pat.friday_absent_count || 0}</span></div>
                                  {pat.weekday_pattern_flag && (
                                    <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold">
                                      <AlertTriangle className="w-3 h-3" /> Repeat-weekday pattern — follow up
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div>
                                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold mb-1">Late & Absent Dates</div>
                                <div className="text-xs text-slate-600 space-y-1">
                                  {(pat.late_dates || []).length > 0 && <div><span className="font-semibold text-amber-700">Late:</span> {(pat.late_dates || []).map((d) => `${d} (${weekday(d)})`).join(", ")}</div>}
                                  {(pat.absence_dates || []).length > 0 && <div><span className="font-semibold text-red-700">Absent:</span> {(pat.absence_dates || []).map((d) => `${d} (${weekday(d)})`).join(", ")}</div>}
                                  {(pat.late_dates || []).length === 0 && (pat.absence_dates || []).length === 0 && <div className="text-slate-400">None</div>}
                                </div>
                              </div>
                            </div>
                            <StaffNoteEditor memberId={p.member_id} seasonId={seasonId} isAdmin={isAdmin} />
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </>)}
      </main>
    </div>
  );
}

/**
 * Staff Performance / Tiers report card.
 *
 * Bundles two actions: View Tier Table (toggles the inline season table)
 * and Download Performance Excel/CSV. Living on the Reports hub instead of
 * the page header keeps the layout consistent — every report is a card.
 */
function StaffPerformanceReportCard({ downloadFile, seasonId, onToggleTable, tableOpen }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 mb-5"
         data-testid="staff-performance-report-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-2">
          <BarChart3 className="w-5 h-5 text-violet-600 mt-0.5" />
          <div>
            <h2 className="font-display text-base font-bold text-slate-900">
              Staff Performance & Tiers
            </h2>
            <div className="text-xs text-slate-500 max-w-xl">
              Season-long attendance and on-time rates for every staff member, grouped into performance tiers.
              View the table inline or download the full Excel / CSV.
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onToggleTable}
            className="btn-touch inline-flex items-center gap-2 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold"
            data-testid="staff-performance-view-btn"
          >
            {tableOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {tableOpen ? "Hide table" : "View tier table"}
          </button>
          <button
            onClick={() => downloadFile("/reports/staff-performance/csv", { season_id: seasonId }, `RRDC_Staff_Performance_${seasonId}.csv`)}
            className="btn-touch inline-flex items-center gap-2 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-semibold"
            data-testid="staff-performance-csv"
          >
            <FileText className="w-4 h-4" /> CSV
          </button>
          <button
            onClick={() => downloadFile("/reports/staff-performance/xlsx", { season_id: seasonId }, `RRDC_Staff_Performance_${seasonId}.xlsx`)}
            className="btn-touch inline-flex items-center gap-2 px-4 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold"
            data-testid="staff-performance-xlsx"
          >
            <FileSpreadsheet className="w-4 h-4" /> Download Excel
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Absences & Lateness date-range download card.
 *
 * Sits at the top of the Reports page. Lets the user pick a single date
 * (default = today) or any custom date range, then downloads a single
 * Excel workbook with two sheets — Planned Absences + Planned Lateness —
 * sorted by Last, First. Backend enforces division scoping for non-admins.
 */
function AbsenceLatenessReportCard({ downloadFile }) {
  const today = () => new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(today());
  const [downloading, setDownloading] = useState(false);

  const setSingleDay = (d) => { setStartDate(d); setEndDate(d); };

  const downloadReport = async () => {
    if (!startDate || !endDate) {
      toast.error("Please pick a start and end date.");
      return;
    }
    if (startDate > endDate) {
      toast.error("Start date must be on or before the end date.");
      return;
    }
    setDownloading(true);
    try {
      const filename =
        startDate === endDate
          ? `RRDC_Absences_Lateness_${startDate}.xlsx`
          : `RRDC_Absences_Lateness_${startDate}_to_${endDate}.xlsx`;
      await downloadFile(
        "/reports/absences-latenesses/xlsx",
        { start_date: startDate, end_date: endDate },
        filename,
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 mb-5"
         data-testid="absence-lateness-report-card">
      <div className="flex flex-wrap items-center gap-3 justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-amber-600" />
          <div>
            <h2 className="font-display text-base font-bold text-slate-900">
              Absences & Lateness Report
            </h2>
            <div className="text-xs text-slate-500">
              Pick a single day or a date range. Names are sorted Last, First.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setSingleDay(today())}
            className="px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-[0.12em] bg-slate-100 hover:bg-slate-200 text-slate-700"
            data-testid="absence-quick-today"
          >Today</button>
          <button
            type="button"
            onClick={() => {
              const d = new Date(); d.setDate(d.getDate() - 1);
              setSingleDay(d.toISOString().slice(0, 10));
            }}
            className="px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-[0.12em] bg-slate-100 hover:bg-slate-200 text-slate-700"
            data-testid="absence-quick-yesterday"
          >Yesterday</button>
          <button
            type="button"
            onClick={() => {
              const end = new Date();
              const start = new Date(); start.setDate(end.getDate() - 6);
              setStartDate(start.toISOString().slice(0, 10));
              setEndDate(end.toISOString().slice(0, 10));
            }}
            className="px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-[0.12em] bg-slate-100 hover:bg-slate-200 text-slate-700"
            data-testid="absence-quick-7d"
          >Last 7 days</button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">From</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
            data-testid="absence-start-date"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">To</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
            data-testid="absence-end-date"
          />
        </label>
        <button
          onClick={downloadReport}
          disabled={downloading}
          className="btn-touch inline-flex items-center gap-2 px-4 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-bold text-sm disabled:opacity-50"
          data-testid="absence-download-btn"
        >
          <Download className="w-4 h-4" />
          {downloading ? "Building report…" : "Download report"}
        </button>
      </div>
    </div>
  );
}


/**
 * Absence Request Form — Submitted? report card.
 *
 * Excel: Last Name · First Name · Submitted? (Yes / No). One row per active
 * staff member. Color-coded so "No" rows pop. Tells admins who still needs
 * to fill out the in-app planned-absence form.
 */
function AbsenceFormStatusCard({ downloadFile }) {
  const [downloading, setDownloading] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const download = async () => {
    setDownloading(true);
    try {
      await downloadFile(
        "/reports/absence-form-status/xlsx",
        {},
        `RRDC_Absence_Form_Status_${today}.xlsx`,
      );
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 mb-5"
         data-testid="absence-form-status-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-2">
          <FileSpreadsheet className="w-5 h-5 text-teal-600 mt-0.5" />
          <div>
            <h2 className="font-display text-base font-bold text-slate-900">
              Absence Request Form — Submitted?
            </h2>
            <div className="text-xs text-slate-500 max-w-xl">
              Every active staff member with a Yes / No on whether they&apos;ve filled out the in-app Absence Request Form.
              Sorted by last name. &quot;No&quot; rows are highlighted red so you can chase laggards at a glance.
            </div>
          </div>
        </div>
        <button
          onClick={download}
          disabled={downloading}
          className="btn-touch inline-flex items-center gap-2 px-4 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm disabled:opacity-50"
          data-testid="absence-form-status-download-btn"
        >
          <Download className="w-4 h-4" />
          {downloading ? "Building report…" : "Download report"}
        </button>
      </div>
    </div>
  );
}


/**
 * One-click download card for the Planned-Absences Grid report.
 *
 * Excel layout: rows = staff with ≥1 planned absence; columns = the 38
 * camp days. Cell value is "Yes" when planned off that date, blank
 * otherwise. Great for printing at the start of a session to see who's
 * planning to miss which day.
 */
function PlannedAbsencesGridCard({ downloadFile }) {
  const [downloading, setDownloading] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const download = async () => {
    setDownloading(true);
    try {
      await downloadFile(
        "/reports/planned-absences-grid/xlsx",
        {},
        `RRDC_Planned_Absences_Grid_${today}.xlsx`,
      );
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 mb-5"
         data-testid="planned-absences-grid-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-2">
          <FileSpreadsheet className="w-5 h-5 text-rose-600 mt-0.5" />
          <div>
            <h2 className="font-display text-base font-bold text-slate-900">
              Planned Absences Grid
            </h2>
            <div className="text-xs text-slate-500 max-w-xl">
              One row per staff member who has at least one planned absence. 38 columns — one per camp day.
              "Yes" in the cell when they're planned off that date.
            </div>
          </div>
        </div>
        <button
          onClick={download}
          disabled={downloading}
          className="btn-touch inline-flex items-center gap-2 px-4 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm disabled:opacity-50"
          data-testid="planned-grid-download-btn"
        >
          <Download className="w-4 h-4" />
          {downloading ? "Building grid…" : "Download grid"}
        </button>
      </div>
    </div>
  );
}

